<template>
  <div class="hearingAidSales__location-summary">
    <div class="sales-report__wrapper">

      <div class="css-grid sales-report__header">
        <div class="sales-report__header-name">
          <div>Locations Summary</div>
        </div>
      </div>

      <div class="location-summary--table-wrapper">

        <table class="location-summary__table">
          <tr>
            <td class="table-field--chart">
              <LocationSummaryLegend :location="biggestLocation" :max="biggestLocation.totals.units" :min="chartMin"></LocationSummaryLegend>
            </td>
            <td v-for="location in validLocations">
              <LocationSummaryChart :location="location" :axis="false" :max="biggestLocation.totals.units" :min="chartMin"></LocationSummaryChart>
            </td>
            <td v-for="location in extraLocations">&nbsp;</td>
          </tr>
          <tr>
            <td>&nbsp;</td>
            <td v-for="location in validLocations" v-bind:key="location.location_id" class="table-field--name">{{ location.name }}</td>
            <td v-for="location in extraLocations">&nbsp;</td>
          </tr>

          <TableRow
              :valid-locations="validLocations"
              :extra-locations="extraLocations"
              title="Premium"
              location-property="premium_units"
          ></TableRow>

          <TableRow
              :valid-locations="validLocations"
              :extra-locations="extraLocations"
              title="Advanced"
              location-property="advanced_units"
          ></TableRow>

          <TableRow
              :valid-locations="validLocations"
              :extra-locations="extraLocations"
              title="Basic"
              location-property="basic_units"
          ></TableRow>

          <TableRow
              :valid-locations="validLocations"
              :extra-locations="extraLocations"
              title="Entry"
              location-property="entry_units"
          ></TableRow>

          <TableRow
              :valid-locations="validLocations"
              :extra-locations="extraLocations"
              title="Access"
              location-property="access_units"
          ></TableRow>

          <TableRow
              :valid-locations="validLocations"
              :extra-locations="extraLocations"
              title="CROS"
              location-property="cros_units"
          ></TableRow>

          <TableRow
              :valid-locations="validLocations"
              :extra-locations="extraLocations"
              title="OTC"
              location-property="otc_units"
          ></TableRow>

          <TableRow
              :valid-locations="validLocations"
              :extra-locations="extraLocations"
              title="Unspecified"
              location-property="unspecified_units"
          ></TableRow>

          <tr class="total-row">
            <td>
              <div class="grid">
                <div class="grid__col--12 total-col h-right-align">TOTAL UNITS</div>
              </div>
            </td>
            <td v-for="location in validLocations" v-bind:key="location.location_id">{{ location.totals.units }}</td>
            <td v-for="location in extraLocations">&nbsp;</td>
          </tr>

          <tr class="total-row">
            <td>
              <div class="grid">
                <div class="grid__col--12 total-col h-right-align">TOTAL SALES</div>
              </div>
            </td>
            <td v-for="location in validLocations" v-bind:key="location.location_id">${{ currency(location.totals.sales) }}</td>
            <td v-for="location in extraLocations">&nbsp;</td>
          </tr>

          <tr class="total-row">
            <td>
              <div class="grid">
                <div class="grid__col--12 total-col h-right-align">ASP</div>
              </div>
            </td>
            <td v-for="location in validLocations" v-bind:key="location.location_id">${{ currency(divide(location.totals.sales, location.totals.units)) }}</td>
            <td v-for="location in extraLocations">&nbsp;</td>
          </tr>
        </table>
      </div>

    </div>
  </div>
</template>

<script>
  import lodash from 'lodash';
  import { mapGetters } from 'vuex';
  import ChartCustom from '../../general/ChartCustom.vue';
  import LocationSummaryChart from './location-summary/LocationSummaryChart.vue';
  import LocationSummaryLegend from './location-summary/LocationSummaryLegend.vue';
  import TableRow from './location-summary/TableRow.vue';
  import DataMethods from '../../utils/DataMethods';

  export default {
    name: 'ReportLocationSummary',
    mixins: [DataMethods],
    components: {
      ChartCustom,
      TableRow,
      LocationSummaryChart,
      LocationSummaryLegend,
    },
    computed: {
      ...mapGetters('purchaseActivityFlash', ['locations']),
      unitTypes() {
        return [
          'premium_units',
          'advanced_units',
          'basic_units',
          'entry_units',
          'access_units',
          'cros_units',
          'unspecified_units',
        ]
      },
      extraLocations() {
        if (this.validLocations.length < 12) {
          return [...Array(12 - this.validLocations.length)];
        }
        return [];
      },
      validLocations() {
        return lodash.filter(this.locations, (location) => {
          return location.name !== '';
        });
        return lodash.filter(this.locations, (location) => {
          return lodash.reduce(location.totals, (sum, total, key) => {
            return this.unitTypes.includes(key) ? sum + total : sum;
          }, 0) > 0;
        });
      },
      biggestLocation() {
        return lodash.maxBy(this.locations, (location) => {
          return lodash.reduce(location.totals, (sum, total, key) => {
            return this.unitTypes.includes(key) ? sum +  Math.abs(total) : sum;
          }, 0);
        });
      },
      chartMin() {
        const min = lodash.minBy(this.locations, (location) => {
          return lodash.reduce(location.totals, (sum, total, key) => {
            return this.unitTypes.includes(key) && total < 0 ? sum +  Math.abs(total) : sum;
          }, 0);
        });
        return lodash.reduce(min, (sum, total, key) => {
          return this.unitTypes.includes(key) && total < 0 ? sum +  Math.abs(total) : sum;
        }, 0)
      },
      formattedData() {
        return lodash.map(this.validLocations, (location) => {
            return {
              ...location.totals,
              name: location.name,
              abs_units: lodash.reduce(location.totals, (sum, total, key) => {
                return this.unitTypes.includes(key) ? sum +  Math.abs(total) : sum;
              }, 0)
            }
        });
      },
      chartWidth() {
        const minWidth = 300;
        const width = (this.locations.length > 0) ? (this.locations.length * 110) + 42 : minWidth
        return width > minWidth ? width : minWidth;
      },
      dimensions() {
        return {
          height: 320,
          width: this.chartWidth,
        };
      },
      chartConfig() {
        const formattedData = this.formattedData;
        const self = this;
        return {
          size: {
            height: 320,
            width: this.chartWidth,
          },
          data: {
            json: formattedData,
            type: 'bar',
            labels: {
              format: function(v, id, i, j) {
                if (v > 0 && id != undefined) {
                  if (id != undefined) {
                    let percRaw = parseInt(v) / (parseInt(formattedData[i]['abs_units']) || 1);
                    let perc = self.round(percRaw * 100);
                    return perc+'%';
                  }
                  return v;
                }
                return '';
              }
            },
            names: {
              premium_units: 'Premium',
              advanced_units: 'Advanced',
              basic_units: 'Basic',
              entry_units: 'Entry',
              access_units: 'Access',
              cros_units: 'CROS',
              otc_units: 'OTC',
              unspecified_units: 'Unspecified'
            },
            keys: {
              x: 'name',
              value: this.unitTypes.reverse(),
            },
            groups: [
              this.unitTypes.reverse(),
            ],
            colors: {
              premium_units: '#1EAEFF',
              advanced_units: '#80BD4D',
              basic_units: '#D87E00',
              entry_units: '#E9CA1E',
              access_units: '#F8076E',
              cros_units: '#1B75BB',
              unspecified_units: '#ADB1B9',
            },
            order: null,
          },
          axis: {
            x: {
              type: 'category',
              show: false,
            },
            y: {
              show: true
            },
            y2: {
              show: false
            },
          },
          grid: {
            y: {
              lines: [
                {value: 0, text: ''},
              ]
            }
          },
          bar: {
            space: 0.3,
            width: 110
          },
          legend: {
            show: false,
            position: 'inset',
            inset: {
              anchor: 'top-left',
              x: 0,
              y: -65,
              step: 2
            },
            hide: ['units']
          },
          tooltip: {
            format: {
              value: d3.format(",.0f")
            },
            contents: function(d, defaultTitleFormat, defaultValueFormat, color) {
              let $$ = this;
              let config = $$.config;
              let titleFormat = config.tooltip_format_title || defaultTitleFormat;
              let nameFormat = config.tooltip_format_name || function (name) { return name; };
              let valueFormat = config.tooltip_format_value || defaultValueFormat;

              return lodash.reduce(d, (text, data) => {
                if (!data && (data.value || data.value === 0)) { return text; }

                if (text === '') {
                  let title = titleFormat ? titleFormat(data.x) : data.x;
                  text = "<table class='" + $$.CLASS.tooltip + "'>" + (title || title === 0 ? "<tr><th colspan='2'>" + title + "</th></tr>" : "");
                }

                let name = nameFormat(data.name);
                let value = valueFormat(data.value, data.ratio, data.id, data.index);
                let bgcolor = $$.levelColor ? $$.levelColor(data.value) : color(data.id);

                text += "<tr class='" + $$.CLASS.tooltipName + "-" + d.id + "'>";
                text += "<td class='name'><span style='background-color:" + bgcolor + "'></span>" + name + "</td>";
                text += "<td class='value'>" + value + "</td>";
                text += "</tr>";
                return text;
              }, '') + "</table>";
            }
          }
        };
      }
    },
  }
</script>

<style lang="scss">
  .hearingAidSales__location-summary {
    .c3-custom-chart {
      min-width: unset;
    }
    .table-field--chart .c3-chart {
      display: none;
    }
    .table-field--chart {
      position: relative;
    }
    .table-field--chart .c3-custom-chart {
      margin-left: 67%;
    }
  }
</style>
